import { observable, action, computed } from "mobx";
import {
  distanceInMeters,
  escapeRegex,
  resolveLocation,
  Types,
  debounce,
} from "@citychallenge/common";
import { CityModel } from "./city.model";
import { CountryModel } from "./country.model";
import { Preference } from "../services/storage";

interface TranslatableName {
  translatedName: string;
}

export class CityStore {
  constructor(
    private cityIdPreference: Preference<string>,
    private lastKnownLocation: Preference<Types["GeoLocation"]>
  ) {}

  @observable
  public cities: CityModel[] = [];

  @action
  public setCities(cities: CityModel[]) {
    this.cities = cities;
  }

  @computed
  public get activeCityId() {
    return this.cityIdPreference.value;
  }

  public findCity(id: string): CityModel | undefined {
    return this.cities.find((c) => c.id === id);
  }

  public findCityByIdOrReturnClosest(id?: string) {
    return this.cities.find((c) => c.id === id) || this.findClosestCity();
  }

  public findClosestCity() {
    const { value: userLocation } = this.lastKnownLocation;

    const firstCity = this.cities.length ? this.cities[0] : undefined;
    if (!userLocation) return firstCity;

    const citiesWithDistance = this.cities
      .filter((city) => !!city.coordinates)
      .map((city) => {
        return {
          distance: distanceInMeters(
            resolveLocation(city.coordinates!.coordinates),
            userLocation
          ),
          city,
        };
      });

    citiesWithDistance.sort((a, b) => {
      return a.distance - b.distance;
    });

    return citiesWithDistance[0]?.city || firstCity;
  }

  /**
   * Get currently active city. If it's not set explicity through
   * the preference service, the value will be equal to first city in the ListItem
   * returned by the server
   */
  @computed
  public get activeCity(): CityModel | undefined {
    const { activeCityId } = this;
    return this.findCityByIdOrReturnClosest(activeCityId);
  }

  public setActiveCity = (city: { id: string }) => {
    this.cityIdPreference.setValue(city.id);
  };

  private translatedNameSort = (a: TranslatableName, b: TranslatableName) => {
    const first = a.translatedName.toLowerCase();
    const second = b.translatedName.toLowerCase();

    return first < second ? -1 : 1;
  };

  @observable
  public searchFilter = "";

  @action
  public setSearchFilter = (value: string) => {
    this.searchFilter = value;
  };

  public setSearchFilterDebounced = debounce(this.setSearchFilter, 300);

  public resetSearchFilter = () => {
    this.setSearchFilter("");
  };

  @computed
  public get searchRegex() {
    return this.searchFilter.length
      ? new RegExp(escapeRegex(this.searchFilter), "i")
      : undefined;
  }

  @computed
  public get filteredCities() {
    const { cities, searchRegex } = this;
    const sorted = cities.slice().sort(this.translatedNameSort);

    if (searchRegex) {
      return sorted.filter((c) => c.contains(searchRegex));
    }

    return sorted;
  }

  @computed
  public get filteredCountries() {
    const countryMap: { [key: string]: CityModel[] } = {};

    for (const city of this.filteredCities) {
      if (!city.country?.id) continue;
      const countryId = city.country.id;
      const existing = countryMap[countryId];

      if (existing) {
        existing.push(city);
      } else {
        countryMap[countryId] = [city];
      }
    }

    const countries = Object.values(countryMap).map((cities) => {
      const country = cities[0].country!;
      return new CountryModel({
        ...country,
        cities,
      });
    });
    return countries.sort(this.translatedNameSort);
  }
}
